// Re-runs a #fragment landing once after window load + geometry settle, so the target lands
// where the final scroll padding puts it. Page-scoped: at most one pending correction per
// page regardless of how many toolbars schedule it, surviving individual toolbar unmounts
// (fragment and target are re-validated from the live DOM). Any user scroll intent cancels.
const FRAGMENT_CANCEL_EVENTS = ["wheel", "touchstart", "pointerdown", "keydown"];
let cancelPendingCorrection = null;

function currentFragment() {
  try {
    return decodeURIComponent(window.location.hash.slice(1));
  } catch {
    // Malformed percent-encoding in the hash — nothing to correct.
    return "";
  }
}

export function scheduleFragmentLandingCorrection() {
  if (cancelPendingCorrection) {
    return;
  }

  // On back/forward the browser restores the previous scroll position; correcting to the
  // fragment would fight that restoration.
  if (window.performance?.getEntriesByType?.("navigation")?.[0]?.type === "back_forward") {
    return;
  }

  const fragment = currentFragment();
  if (!fragment || !document.getElementById(fragment)) {
    return;
  }

  let cancelled = false;
  const cancel = () => {
    cancelled = true;
    FRAGMENT_CANCEL_EVENTS.forEach((type) => window.removeEventListener(type, cancel, { capture: true }));
    window.removeEventListener("load", correct);
    cancelPendingCorrection = null;
  };
  FRAGMENT_CANCEL_EVENTS.forEach((type) => window.addEventListener(type, cancel, { capture: true, passive: true }));
  cancelPendingCorrection = cancel;

  const correct = () => {
    // Two frames let the rAF-debounced geometry writes (scroll padding) flush first.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        // The user navigating to a different fragment in the meantime also voids the
        // correction — never scroll back to a hash that is no longer current.
        if (cancelled || currentFragment() !== fragment) {
          cancel();
          return;
        }
        const target = document.getElementById(fragment);
        if (!target) {
          cancel();
          return;
        }
        target.scrollIntoView();

        // The first pass can race the toolbar's own first layout (the bar height reaches the
        // scroll padding a frame later) — verify against the settled padding and nudge once.
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (cancelled || currentFragment() !== fragment) {
              cancel();
              return;
            }
            cancel();
            // Re-read the target — it may have been replaced since the first pass.
            const settledTarget = document.getElementById(fragment);
            if (!settledTarget) {
              return;
            }
            const padding = Number.parseFloat(window.getComputedStyle(document.documentElement).scrollPaddingTop);
            if (!Number.isNaN(padding) && Math.abs(settledTarget.getBoundingClientRect().top - padding) > 1) {
              settledTarget.scrollIntoView();
            }
          }),
        );
      }),
    );
  };

  if (document.readyState === "complete") {
    correct();
  } else {
    window.addEventListener("load", correct, { once: true });
  }
}
